import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CTABand } from "@/components/CTABand";
import { RESEARCH_ITEMS } from "@/data/content";

const FORMATS = [
  { title: "Briefings", copy: "Concise, sourced analysis on developing stories and policy shifts." },
  { title: "Comparative Studies", copy: "Side-by-side examination of African and Caribbean institutions and policy." },
  { title: "Data Explainers", copy: "Verified data presented plainly, with methods and sources disclosed." },
  { title: "Institutional Trackers", copy: "Living records of commitments announced, implemented or stalled." },
  { title: "Policy Analysis", copy: "Practical analysis written for policymakers, journalists and the public." },
];

export default function ResearchPage() {
  return (
    <main data-testid="research-page">
      <PageHero
        kicker="Research and Policy"
        title="Analysis Built for Public Use"
        copy="Briefings, comparative studies, data explainers, institutional trackers and practical policy analysis — documented openly and written to be used."
      />
      <section className="bg-sand py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-6">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">Featured Research</p>
              <div>
                {RESEARCH_ITEMS.map((item, i) => (
                  <Link
                    key={item.title}
                    to={item.to}
                    data-testid={`research-page-item-${i}`}
                    className="group flex items-baseline gap-5 border-b border-atlantic/10 py-6 transition-colors first:border-t hover:bg-white"
                  >
                    <span className="font-serif text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <span className="flex-1">
                      <span className="block font-serif text-lg leading-snug text-atlantic transition-colors group-hover:text-ocean sm:text-xl">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">{item.type}</span>
                    </span>
                    <ArrowUpRight size={16} className="shrink-0 text-atlantic/40 transition-colors group-hover:text-gold" />
                  </Link>
                ))}
              </div>
              <p className="mt-8 text-sm leading-relaxed text-slate-500">
                Trackers and dossiers are updated as cooperation commitments are announced, implemented or stall.
              </p>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">Research Formats</p>
              <div className="mt-6 space-y-4">
                {FORMATS.map((f) => (
                  <div key={f.title} data-testid={`research-format-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border border-atlantic/10 bg-white p-6">
                    <h3 className="font-serif text-lg text-atlantic">{f.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.copy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <CTABand
        kicker="Research Partnerships"
        title="Research With Us"
        copy="Atlantic Bridge collaborates with universities, policy institutes and civil-society researchers on shared questions across the Atlantic."
        buttonLabel="Propose a Research Partnership"
        to="/contact"
      />
    </main>
  );
}
